import React from "react";
import { Box, Chip, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ConvoTrace from "./ConvoTrace";
import ParityGapsChip from "./ParityGapsChip";

const OUTCOME_COLOR = {
  match: "success",
  clarification: "info",
  brick: "error",
  error: "error",
};

const formatPreset = (preset) => {
  const overrides = { convoengine_plus: "ConvoEngine Plus", cws: "CWS" };
  return overrides[preset?.toLowerCase()] || preset;
};

const formatScore = (m) => (m.is_exact ? "exact" : Number(m.score).toFixed(3));

// One preset's column: outcome chip + its ranked matches (usually 1, up to 4
// on a clarification). This is the live, single-question comparison; the
// batch/goldset version lives in the Evaluations report.
const PresetColumn = ({ preset, result, info = {}, expanded = false }) => {
  const channels = info.channels || [];
  const knownGaps = info.known_gaps || [];
  return (
    <Box sx={{ flex: 1, minWidth: 220 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap" }}>
        <Typography variant="subtitle2">{formatPreset(preset)}</Typography>
        <Chip size="small" color={OUTCOME_COLOR[result.outcome] || "default"} label={result.outcome} />
        <ParityGapsChip gaps={knownGaps} />
      </Stack>
      {channels.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
          {channels.join(" + ")}
        </Typography>
      )}
      {(result.matches || []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          (no match)
        </Typography>
      ) : (
        result.matches.map((m, i) => (
          <Box key={i} sx={{ mb: 0.5 }}>
            <Typography variant="body2">{m.character_speech}</Typography>
            <Typography variant="caption" color="text.secondary">
              {m.meaning || "—"} · {formatScore(m)}
            </Typography>
          </Box>
        ))
      )}

      {/* Same switch as the single-answer view: the trace always renders, and
        "Advanced insights" only decides whether it starts expanded. */}
      {result.retrieval_trace && <ConvoTrace trace={result.retrieval_trace} expanded={expanded} inset={false} />}
    </Box>
  );
};

const ConvoCompareResult = (props) => {
  const { response, presetInfo = {}, expanded = false } = props;
  const { question, presets = {}, agreement } = response;
  const presetNames = Object.keys(presets);

  return (
    <ListItem alignItems="flex-start" sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
      <ListItemIcon sx={{ minWidth: 36, mt: 1 }}>
        <CompareArrowsIcon color={agreement?.all_agree ? "success" : "warning"} fontSize="small" />
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>User:</strong> {question}
            </Typography>
            <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
              {presetNames.map((preset) => (
                <PresetColumn
                  key={preset}
                  preset={preset}
                  result={presets[preset]}
                  info={presetInfo[preset]}
                  expanded={expanded}
                />
              ))}
            </Stack>
            <Chip
              size="small"
              sx={{ mt: 1 }}
              color={agreement?.all_agree ? "success" : "warning"}
              label={agreement?.all_agree ? "Presets agree" : "Presets disagree"}
            />
          </Box>
        }
      />
    </ListItem>
  );
};

export default ConvoCompareResult;
